/**
 * Router-compat shim — bridges @/lib/router-compat v6 call sites to
 * @tanstack/react-router without hand-rewriting every component.
 * This is the same load-bearing pattern used in Klar's dev-copy migration.
 */
import {
  useNavigate as tsNavigate,
  useLocation as tsLocation,
  useParams as tsParams,
  useSearch as tsSearch,
  useRouter,
  Link as TSLink,
  Navigate as TSNavigate,
  Outlet as TSOutlet,
} from "@tanstack/react-router";
import {
  useMemo,
  useCallback,
  useContext,
  useEffect,
  useState,
  createContext,
  forwardRef,
  Children,
  isValidElement,
  type ComponentProps,
  type ReactNode,
  type ReactElement,
} from "react";

/** Set only by the test-only `MemoryRouter` below; null in the real app. */
const MemoryLocationContext = createContext<{
  pathname: string;
  searchStr: string;
  hash: string;
  state: unknown;
  navigate: (to: string, options?: { replace?: boolean; state?: unknown }) => void;
} | null>(null);

// ---------- shared URL parsing ----------

function parseTo(to: string): { pathname: string; search?: Record<string, string>; hash?: string } {
  const [beforeHash, hashStr] = (to ?? "").split("#");
  const [pathname, searchStr] = beforeHash.split("?");
  return {
    // react-router keeps the current path for search-only ("?a=1") and
    // hash-only ("#section") targets; TanStack's "." means current route.
    pathname: pathname || ".",
    search: searchStr ? Object.fromEntries(new URLSearchParams(searchStr)) : undefined,
    hash: hashStr || undefined,
  };
}

// ---------- useNavigate ----------

type NavigateOptions = { replace?: boolean; state?: unknown };

type NavigateFn = {
  (to: string | number, options?: NavigateOptions): void;
  (delta: number): void;
};

export function useNavigate(): NavigateFn {
  const memory = useContext(MemoryLocationContext);
  // Under the test-only MemoryRouter there is no TanStack router in the tree,
  // so these return null/throw; the memory location handles navigation instead.
  let tsNav: ReturnType<typeof tsNavigate> | null = null;
  let router: ReturnType<typeof useRouter> | null = null;
  try {
    tsNav = tsNavigate();
    router = useRouter();
  } catch {
    tsNav = null;
    router = null;
  }
  return useCallback((to: string | number, options?: NavigateOptions) => {
    if (typeof to === "number") {
      router?.history.go(to);
      return;
    }
    if (memory || !tsNav) {
      memory?.navigate(to, options);
      return;
    }
    const { pathname, search, hash } = parseTo(to);
    tsNav({
      to: pathname,
      search: search as never,
      hash,
      state: options?.state as never,
      replace: options?.replace,
    });
  }, [tsNav, router, memory]) as NavigateFn;
}

// ---------- useLocation ----------

export function useLocation() {
  const memory = useContext(MemoryLocationContext);
  // Under the test-only MemoryRouter there is no TanStack router in the tree,
  // so reading the real location throws; fall back to the memory location.
  let realLoc: { pathname: string; searchStr?: string; hash?: string; state?: unknown } | null = null;
  try {
    realLoc = tsLocation();
  } catch {
    realLoc = null;
  }
  const loc: { pathname: string; searchStr: string; hash: string; state: unknown } = memory ?? {
    pathname: realLoc?.pathname ?? "/",
    searchStr: realLoc?.searchStr ?? "",
    hash: realLoc?.hash ?? "",
    state: realLoc?.state ?? null,
  };
  return useMemo(
    () => ({
      pathname: loc.pathname,
      search: loc.searchStr ? `?${loc.searchStr}` : "",
      hash: loc.hash ?? "",
      state: (loc.state ?? null) as unknown,
      key: loc.pathname + (loc.searchStr ?? ""),
    }),
    [loc.pathname, loc.searchStr, loc.hash, loc.state],
  );
}

// ---------- useParams ----------

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  return tsParams({ strict: false } as never) as T;
}


// ---------- useSearchParams (@/lib/router-compat compat) ----------

export function useSearchParams(): [URLSearchParams, (init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams), opts?: { replace?: boolean }) => void] {
  const loc = tsLocation();
  const nav = tsNavigate();
  const router = useRouter();
  const params = useMemo(() => new URLSearchParams(loc.searchStr ?? ""), [loc.searchStr]);
  const setParams = useCallback(
    (
      init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => {
      // Functional updaters read the router's live location, not the render
      // snapshot — react-router passes call-time params, and chained updates
      // within one tick must see each other's writes.
      const live = router.state.location;
      const current = new URLSearchParams(live.searchStr ?? "");
      const next =
        typeof init === "function"
          ? init(current)
          : init instanceof URLSearchParams
            ? init
            : new URLSearchParams(init);
      const searchObj: Record<string, string> = {};
      next.forEach((v, k) => { searchObj[k] = v; });
      nav({ to: live.pathname, search: searchObj as never, replace: opts?.replace });
    },
    [nav, router],
  );
  return [params, setParams];
}

// ---------- Link ----------

type LinkProps = Omit<ComponentProps<typeof TSLink>, "to"> & {
  to: string;
  replace?: boolean;
  state?: unknown;
  children?: ReactNode;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, replace, state, children, ...rest },
  ref,
) {
  const memory = useContext(MemoryLocationContext);
  const { pathname, search, hash } = parseTo(to);
  // Test-only MemoryRouter has no TanStack router, so render a plain anchor.
  if (memory) {
    const { onClick, ...anchorRest } = (rest ?? {}) as React.AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a
        ref={ref}
        href={to}
        {...anchorRest}
        onClick={(e) => {
          onClick?.(e);
          if (e.defaultPrevented) return;
          e.preventDefault();
          memory.navigate(to, { replace, state });
        }}
      >
        {children}
      </a>
    );
  }
  return (
    <TSLink
      ref={ref as never}
      to={pathname as never}
      search={search as never}
      hash={hash}
      replace={replace}
      state={state as never}
      {...((rest ?? {}) as Record<string, unknown>)}
    >
      {children}
    </TSLink>
  );
});


// ---------- Navigate ----------

export function Navigate({ to, replace, state }: { to: string; replace?: boolean; state?: unknown }) {
  const memory = useContext(MemoryLocationContext);
  const { pathname, search, hash } = parseTo(to);
  useEffect(() => {
    if (memory) memory.navigate(to, { replace, state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memory, to, replace]);
  if (memory) return null;
  return <TSNavigate to={pathname as never} search={search as never} hash={hash} state={state as never} replace={replace} />;
}

// ---------- Outlet ----------

export const Outlet = TSOutlet;

// ---------- NavLink (minimal) ----------

export const NavLink = Link;

// ---------- matchPath (react-router-dom compat, minimal) ----------

type PathPattern = string | { path: string; end?: boolean; caseSensitive?: boolean };

export function matchPath(
  pattern: PathPattern,
  pathname: string,
): { params: Record<string, string>; pathname: string; pattern: PathPattern } | null {
  const { path, end = true, caseSensitive = false } =
    typeof pattern === "string" ? { path: pattern, end: true, caseSensitive: false } : pattern;

  const patternSegs = path.split("/").filter(Boolean);
  const pathSegs = pathname.split("/").filter(Boolean);

  const hasSplat = patternSegs[patternSegs.length - 1] === "*";
  const compareSegs = hasSplat ? patternSegs.slice(0, -1) : patternSegs;

  if (hasSplat ? pathSegs.length < compareSegs.length : end ? pathSegs.length !== compareSegs.length : pathSegs.length < compareSegs.length) {
    return null;
  }

  const params: Record<string, string> = {};
  for (let i = 0; i < compareSegs.length; i++) {
    const p = compareSegs[i];
    const s = pathSegs[i];
    if (p.startsWith(":")) {
      params[p.slice(1)] = decodeURIComponent(s);
    } else if (caseSensitive ? p !== s : p.toLowerCase() !== s.toLowerCase()) {
      return null;
    }
  }
  if (hasSplat) params["*"] = pathSegs.slice(compareSegs.length).join("/");
  return { params, pathname, pattern };
}

/**
 * ---------- Test-only in-memory routing ----------
 *
 * The suite mounts components inside `<MemoryRouter>` / `<Routes>` / `<Route>`
 * (the v6 shape those tests were written against). App code uses TanStack
 * Router directly, so none of this ships in the client bundle — but keeping the
 * shim here means the existing tests keep importing it from one place.
 */

export function MemoryRouter({
  initialEntries,
  children,
}: {
  initialEntries?: string[];
  children?: ReactNode;
}) {
  const [entry, setEntry] = useState(initialEntries?.[0] ?? "/");
  const [state, setState] = useState<unknown>(null);

  const value = useMemo(() => {
    const [pathAndSearch, hash = ""] = entry.split("#");
    const [pathname, searchStr = ""] = pathAndSearch.split("?");
    return {
      pathname: pathname || "/",
      searchStr,
      hash: hash ? `#${hash}` : "",
      state,
      navigate: (to: string, options?: { replace?: boolean; state?: unknown }) => {
        setEntry(to);
        setState(options?.state ?? null);
      },
    };
  }, [entry, state]);

  return <MemoryLocationContext.Provider value={value}>{children}</MemoryLocationContext.Provider>;
}

export interface RouteProps {
  path: string;
  element: ReactNode;
}

/** Declarative marker consumed by `Routes`; renders nothing on its own. */
export function Route(_props: RouteProps): null {
  return null;
}

/** Renders the first child `<Route>` whose path matches the memory location. */
export function Routes({ children }: { children?: ReactNode }) {
  const memory = useContext(MemoryLocationContext);
  const pathname = memory?.pathname ?? "/";

  const routes = Children.toArray(children).filter(
    (child): child is ReactElement<RouteProps> => isValidElement(child),
  );

  for (const route of routes) {
    const { path, element } = route.props;
    if (!path) continue;
    if (matchPath({ path, end: !path.endsWith("/*") }, pathname)) return <>{element}</>;
  }
  return null;
}
